import type { PhaseRecord } from '~/models/phase'
import type { ProjectRecord } from '~/models/project'
import { planDomainBackfill } from '~/storage/migrations/backfillDomains'
import { SCHEMA_V3_STORES, STORE } from '~/storage/schema'
import type { BackupPayload } from '../format'
import type { PayloadMigration } from './index'

/**
 * Version 3 — the paired counterpart of the database migration (D-031, D-092).
 *
 * A version-2 backup restored into a version-3 database would otherwise arrive
 * with none of the seven new stores and no domain goal anywhere, and no upgrade
 * would ever fire to fix it: the live database is already at 3. The restored
 * project would show no domains at all — a plan the user apparently never made —
 * and nothing would report a failure.
 *
 * The decision — one body-composition goal per project, status following the
 * project, cardio and mobility deliberately absent — is `planDomainBackfill`,
 * shared verbatim with `storage/migrations/v3.ts`. All that differs here is the
 * plumbing: arrays in memory instead of requests in a `versionchange`
 * transaction. A merge-blocking test asserts the two registries stay in step, and
 * a paired-equivalence test asserts the two paths produce the same records from
 * the same input.
 *
 * **Every new store must appear, even empty.** Validation checks the payload
 * against the current schema, where a missing store is a gap rather than an empty
 * one. `connections` is included here for that reason and is then always empty in
 * practice — a backup never carries credentials, so a restore leaves every
 * connection disconnected.
 */
export const payloadMigrationV3: PayloadMigration = {
  to: 3,
  describe:
    'Add the seven version-3 stores and give every existing project a body-composition domain goal',
  migrate(payload: BackupPayload): BackupPayload {
    const plan = planDomainBackfill({
      projects: (payload.stores[STORE.projects] ?? []) as ProjectRecord[],
      phases: (payload.stores[STORE.phases] ?? []) as PhaseRecord[],
    })

    /* Every v3 store starts empty, then the backfill fills the one it has
       something to say about. Deriving the list from the schema rather than
       naming seven strings means a store added to `SCHEMA_V3_STORES` cannot be
       created by the database migration and forgotten here. */
    const empties = Object.fromEntries(SCHEMA_V3_STORES.map((definition) => [definition.name, []]))

    return {
      ...payload,
      stores: {
        ...payload.stores,
        ...empties,
        ...(plan === undefined ? {} : { [STORE.domainGoals]: plan.domainGoals }),
      },
    }
  },
}
